import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { ArrowDown, Zap } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.5 + i * 0.12 },
  }),
};

const Line = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.14em] -mb-[0.14em]">
    <motion.span variants={line} custom={i} className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ flashEnd }) => {
  const ref = useRef(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!flashEnd) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [flashEnd]);
  const left = flashEnd ? flashEnd - now : 0;
  const pad = (n) => String(n).padStart(2, "0");
  const timer = left > 0 ? `${pad(Math.floor(left / 3.6e6))}:${pad(Math.floor((left % 3.6e6) / 6e4))}:${pad(Math.floor((left % 6e4) / 1000))}` : null;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} id="top" className="relative min-h-[100svh] overflow-hidden pt-44 md:pt-56">
      {/* Autonomous decorative orbs — move on their own, not with mouse or scroll */}
      <motion.div
        aria-hidden
        animate={{ y: [0, -30, 0], x: [0, 18, 0] }}
        transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute -left-24 top-40 h-72 w-72 rounded-full bg-sage/15 blur-3xl"
      />
      <motion.div
        aria-hidden
        animate={{ y: [0, 26, 0], x: [0, -20, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute right-10 top-24 h-64 w-64 rounded-full bg-terra/15 blur-3xl"
      />
      <motion.div
        aria-hidden
        animate={{ y: [0, 22, 0], x: [0, -14, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute bottom-10 left-1/3 h-60 w-60 rounded-full bg-electric/15 blur-3xl"
      />
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-6 md:grid-cols-12 md:px-10">
        {/* Text */}
        <motion.div
          initial="hidden"
          animate="visible"
          className="md:col-span-7"
        >
          {timer && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              data-testid="hero-flash-timer"
              className="mb-5 inline-flex items-center gap-2 rounded-full bg-terra px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider text-cream shadow-[0_0_22px_rgba(255,46,76,0.55)]"
            >
              <Zap className="h-3.5 w-3.5" strokeWidth={2.5} /> Offerta lampo finisce tra {timer}
            </motion.div>
          )}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="mb-7 flex items-center gap-2 text-xs uppercase tracking-overline text-electric"
            data-testid="hero-overline"
          >
            <motion.span
              aria-hidden
              animate={{ scale: [1, 1.6, 1], opacity: [1, 0.4, 1] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
              className="inline-block h-1.5 w-1.5 rounded-full bg-electric"
            />
            Essenziali mensili · Consegna ricorrente
          </motion.p>

          <h1 className="font-serif text-[15vw] font-light leading-[1.02] tracking-tight text-ink sm:text-7xl lg:text-[7.5rem]">
            <Line i={0}>Il necessario,</Line>
            <Line i={1}>
              <span className="italic text-sage">ogni</span> mese.
            </Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-8 max-w-md text-base leading-relaxed text-ink-soft md:text-lg"
            data-testid="hero-subtitle"
          >
            Casa, pulizia, benessere, vestiario, viaggio, pet e ufficio.
            Tutto ciò che finisce e va rinnovato — riunito in un solo rituale.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("categorie")}
              data-testid="hero-shop-button"
              className="rounded-full bg-sage px-8 py-4 text-sm font-semibold text-cream transition-transform hover:scale-[1.04] hover:bg-sage-deep active:scale-95"
            >
              Esplora le categorie
            </button>
            <button
              onClick={() => scrollToId("box-mensile")}
              data-testid="hero-box-button"
              className="rounded-full border border-electric/60 px-8 py-4 text-sm font-medium text-electric transition-colors hover:bg-electric hover:text-cream"
            >
              Scopri il Box Mensile
            </button>
          </motion.div>
        </motion.div>

        {/* Image */}
        <div className="md:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative h-[52vh] overflow-hidden rounded-[2rem] md:h-[76vh]"
          >
            <motion.img
              style={{ y, scale }}
              src="https://images.unsplash.com/photo-1583329550487-0fa300a4cd1a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
              alt="Interno luminoso e minimale"
              className="h-full w-full object-cover"
            />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="absolute bottom-5 left-5 right-5"
            >
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="rounded-2xl border border-white/40 bg-cream/70 px-5 py-4 backdrop-blur-xl"
              >
                <p className="text-xs uppercase tracking-overline text-ink-soft">Abbonamento flessibile</p>
                <p className="mt-1 font-serif text-2xl text-ink">Sospendi o modifica quando vuoi</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="mx-auto mt-12 flex max-w-7xl items-center gap-3 px-6 text-ink-soft md:px-10"
      >
        <ArrowDown className="h-4 w-4 animate-bounce" strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-overline">Scorri</span>
      </motion.div>
    </section>
  );
};
